import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  deactivateUser,
  listUsers,
  reactivateUser,
  resendInvitation,
  resetUserPassword,
} from '../../api/business/endpoints';
import type { TenantUser, UserStatus } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { useSession } from '../../auth/SessionContext';
import { usePermissions } from '../../auth/usePermissions';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime } from '../../components/format';
import { isSubsetOf } from '../../components/grantMatrix';
import { PageHeader } from '../../components/PageHeader';
import { PasswordResetDialog } from '../../components/PasswordResetDialog';
import { SortableCell } from '../../components/SortableCell';
import { StatusChip } from '../../components/StatusChip';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { CreateUserDialog } from './CreateUserDialog';
import { EditPermissionsDialog } from './EditPermissionsDialog';

type UserSortKey = 'email' | 'displayName' | 'status' | 'grants' | 'lastLoginAt';

const VALUE_OF: Record<UserSortKey, (user: TenantUser) => string | number | null> = {
  email: (user) => user.email,
  displayName: (user) => user.displayName,
  status: (user) => user.status,
  grants: (user) => (user.isOwner ? Number.MAX_SAFE_INTEGER : user.grants.length),
  lastLoginAt: (user) => user.lastLoginAt,
};

type Dialog = { kind: 'create' } | { kind: 'permissions' | 'password'; user: TenantUser };

/**
 * The tenant's users. A row is editable only when the editor could hand out everything the user already
 * holds — the same rule the API applies (BR-11). The owner and the editor's own row never are.
 */
export const UsersPage = () => {
  const { language, t } = useLanguage();
  const { profile } = useSession();
  const permissions = usePermissions();
  const query = useQuery({ queryKey: ['users'], queryFn: listUsers });
  const [invited, setInvited] = useState<Set<string>>(new Set());
  const invite = useAppMutation(
    (userId: string) => resendInvitation(userId).then(() => userId),
    ['users'],
    (userId: string) => setInvited((previous) => new Set(previous).add(userId)),
  );
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const sort = useSort<UserSortKey>('email');
  const deactivate = useAppMutation(deactivateUser, ['users', 'licence-usage']);
  const reactivate = useAppMutation(reactivateUser, ['users', 'licence-usage']);

  const canManageUsers = permissions.can('users.manage');
  const myId = profile?.kind === 'business' ? profile.user.id : null;
  const manageable = (user: TenantUser): boolean =>
    canManageUsers &&
    !user.isOwner &&
    user.id !== myId &&
    (permissions.isOwner || isSubsetOf(user.grants, permissions.grants));

  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    (query.data ?? []).filter(
      (user) =>
        (status === '' || user.status === status) &&
        (needle === '' ||
          [user.email, user.displayName].some((value) => value?.toLocaleLowerCase('sk').includes(needle))),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );

  return (
    <>
      <PageHeader
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        action={
          canManageUsers && permissions.writable ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ kind: 'create' })}>
              {t('users.create')}
            </Button>
          ) : undefined
        }
      />
      <ErrorNotice
        error={query.error ?? deactivate.error ?? reactivate.error}
        onRetry={() => void query.refetch()}
      />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <TextField
            select
            size="small"
            label={t('field.status')}
            value={status}
            onChange={(e) => setStatus(e.target.value as UserStatus | '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="ACTIVE">{t('status.ACTIVE')}</MenuItem>
            <MenuItem value="DISABLED">{t('status.DISABLED')}</MenuItem>
          </TextField>
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="email">
                  {t('field.email')}
                </SortableCell>
                <SortableCell sort={sort} column="displayName">
                  {t('field.displayName')}
                </SortableCell>
                <SortableCell sort={sort} column="grants">
                  {t('users.rights')}
                </SortableCell>
                <SortableCell sort={sort} column="status">
                  {t('field.status')}
                </SortableCell>
                <SortableCell sort={sort} column="lastLoginAt">
                  {t('field.lastLoginAt')}
                </SortableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading ? (
                <TableSkeletonRows columns={6} />
              ) : (
                rows.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{ fontWeight: 560 }}>{user.email}</TableCell>
                    <TableCell>{user.displayName ?? t('common.none')}</TableCell>
                    <TableCell>
                      {user.isOwner ? (
                        <Chip size="small" color="primary" variant="outlined" label={t('role.owner')} />
                      ) : (
                        t('users.grantCount', { count: user.grants.length })
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75}>
                        <StatusChip status={user.status} />
                        {user.invitationPending ? (
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={t('invitation.pending')}
                          />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDateTime(user.lastLoginAt, language)}</TableCell>
                    <TableCell align="right">
                      {manageable(user) ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {permissions.writable ? (
                            <>
                              <Button size="small" onClick={() => setDialog({ kind: 'permissions', user })}>
                                {t('users.permissions')}
                              </Button>
                              <Button size="small" onClick={() => setDialog({ kind: 'password', user })}>
                                {t('password.reset')}
                              </Button>
                              {user.invitationPending && user.status === 'ACTIVE' ? (
                                <Button
                                  size="small"
                                  disabled={invite.isPending || invited.has(user.id)}
                                  onClick={() => invite.mutate(user.id)}
                                >
                                  {invited.has(user.id) ? t('invitation.sent') : t('invitation.resend')}
                                </Button>
                              ) : null}
                            </>
                          ) : null}
                          {user.status === 'ACTIVE' ? (
                            // Allowed on a read-only tenant: locking out a departed employee must not wait (BR-06).
                            <Button size="small" color="warning" onClick={() => deactivate.mutate(user.id)}>
                              {t('users.deactivate')}
                            </Button>
                          ) : permissions.writable ? (
                            <Button size="small" onClick={() => reactivate.mutate(user.id)}>
                              {t('users.reactivate')}
                            </Button>
                          ) : null}
                        </Stack>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && rows.length === 0 ? (
          <EmptyState title={t('users.empty')} detail={t('users.emptyDetail')} />
        ) : null}
      </Paper>

      {dialog?.kind === 'create' ? <CreateUserDialog onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === 'permissions' ? (
        <EditPermissionsDialog user={dialog.user} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'password' ? (
        <PasswordResetDialog
          subject={dialog.user.email}
          onSubmit={(password) => resetUserPassword(dialog.user.id, password)}
          invalidates={['users']}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
};
